import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../pages/AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faBuilding, faPhone, faRupeeSign, faBalanceScale,
    faHistory, faExclamationTriangle, faSearch, faSpinner, faTimes,
    faSave, faPrint, faUsers, faEye, faCreditCard,
    faWallet, faUniversity, faCheckCircle, faExclamationCircle,
    faTag, faCalendarAlt, faList, faFileInvoice, faLandmark,
    faBarcode, faAddressCard, faIdCard, faCopy,
    faMoneyBillWave, faClock, faBank, faInfoCircle,
    faArrowRight, faArrowLeft, faDollarSign, faChartLine,
    faExchangeAlt, faArrowUp, faArrowDown, faCalculator, faChevronLeft, faReceipt
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Helper Functions
// ==============================================
const formatCurrency = (amount, currency = '') => {
    const formatted = parseFloat(amount || 0).toFixed(currency === 'USDT' ? 6 : 2);
    if (currency === 'USDT') return `${formatted} USDT`;
    if (currency === 'PKR') return `Rs. ${formatted}`;
    if (currency === 'DIRHAMS') return `د.إ ${formatted}`;
    if (currency === 'YANS') return `¥ ${formatted}`;
    return `${formatted} ${currency}`;
};

const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('en-IN') : '';

// ==============================================
// Currency Options
// ==============================================
const CURRENCIES = [
    { code: 'PKR', name: 'Pakistani Rupee', symbol: 'Rs.', icon: faRupeeSign },
    { code: 'DIRHAMS', name: 'UAE Dirham', symbol: 'د.إ', icon: faMoneyBillWave },
    { code: 'YANS', name: 'Chinese Yuan', symbol: '¥', icon: faDollarSign },
    { code: 'USDT', name: 'USDT (Tether)', symbol: '₮', icon: faWallet }
];

// ==============================================
// Toast Notification Component
// ==============================================
const ToastNotification = ({ toast, setToast }) => {
    if (!toast.show) return null;
    return (
        <div className={`toast-notification ${toast.type}`}>
            <div className="toast-content">
                <div className="toast-icon">
                    <FontAwesomeIcon icon={toast.type === 'success' ? faCheckCircle : faExclamationCircle} />
                </div>
                <div className="toast-text">
                    <div className="toast-message">{toast.message}</div>
                </div>
                <button className="toast-close" onClick={() => setToast({ show: false })}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
        </div>
    );
};

// ==============================================
// Transfer History Modal
// ==============================================
const TransferHistoryModal = ({ show, onClose, shopId, partnerId, partnerName }) => {
    const [transfers, setTransfers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && shopId) loadTransfers();
    }, [show, shopId, partnerId]);

    const loadTransfers = async () => {
        setLoading(true);
        setError(null);
        try {
            const payload = { action: 'get_transfer_history', shop_id: parseInt(shopId), limit: 100 };
            if (partnerId) payload.partner_id = parseInt(partnerId);
            const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            if (data.status === 'success') setTransfers(data.data || []);
            else setError(data.message || 'Failed to load transfers');
        } catch (err) {
            setError('Network error loading transfers');
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
            <div className="modal-content history-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '1000px', width: '90%', maxHeight: '85vh', overflowY: 'auto' }}>
                <div className="modal-header">
                    <h3><FontAwesomeIcon icon={faHistory} /> Transfer History{partnerName && ` — ${partnerName}`}</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    {loading ? (
                        <div className="loading-state"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
                    ) : error ? (
                        <div className="error-state"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</div>
                    ) : transfers.length === 0 ? (
                        <div className="empty-state">
                            <FontAwesomeIcon icon={faExchangeAlt} />
                            <p>No transfer history found</p>
                        </div>
                    ) : (
                        <div className="transfers-table-container">
                            <table className="transfers-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Partner</th>
                                        <th>Original Amount</th>
                                        <th>Exchange Rate</th>
                                        <th>Converted Amount (USDT)</th>
                                        <th>Reference</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transfers.map(transfer => (
                                        <tr key={transfer.id}>
                                            <td>{formatDate(transfer.transfer_date)}</td>
                                            <td>{transfer.partner_name}</td>
                                            <td className="original-amount">
                                                {formatCurrency(transfer.original_amount, transfer.original_currency)}
                                            </td>
                                            <td>1 {transfer.original_currency} = {transfer.exchange_rate} USDT</td>
                                            <td className="converted-amount">
                                                {formatCurrency(transfer.converted_amount, 'USDT')}
                                            </td>
                                            <td>{transfer.reference_no || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                             </table>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn-primary" onClick={() => window.print()}>
                        <FontAwesomeIcon icon={faPrint} /> Print
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Owner Transactions Modal
// ==============================================
const OwnerTransactionsModal = ({ show, onClose, shopId }) => {
    const [transactions, setTransactions] = useState([]);
    const [totalBalance, setTotalBalance] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (show && shopId) loadTransactions();
    }, [show, shopId]);

    const loadTransactions = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_owner_transactions',
                    shop_id: parseInt(shopId),
                    limit: 100
                })
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            if (data.status === 'success') {
                setTransactions(data.data.transactions || []);
                setTotalBalance(data.data.total_balance || 0);
            } else {
                setError(data.message || 'Failed to load transactions');
            }
        } catch (err) {
            setError('Network error loading transactions');
        } finally {
            setLoading(false);
        }
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={onClose} style={{ zIndex: 2000 }}>
            <div className="modal-content history-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: '900px', width: '90%', maxHeight: '85vh', overflowY: 'auto' }}>
                <div className="modal-header">
                    <h3><FontAwesomeIcon icon={faWallet} /> Owner Transactions</h3>
                    <button className="modal-close" onClick={onClose}>×</button>
                </div>
                <div className="modal-body">
                    <div className="owner-balance-card">
                        <div className="balance-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
                        <div className="balance-content">
                            <div className="balance-label">Total USDT Balance</div>
                            <div className="balance-value">{formatCurrency(totalBalance, 'USDT')}</div>
                        </div>
                    </div>

                    {loading ? (
                        <div className="loading-state"><FontAwesomeIcon icon={faSpinner} spin /> Loading...</div>
                    ) : error ? (
                        <div className="error-state"><FontAwesomeIcon icon={faExclamationTriangle} /> {error}</div>
                    ) : transactions.length === 0 ? (
                        <div className="empty-state">
                            <FontAwesomeIcon icon={faWallet} />
                            <p>No owner transactions found</p>
                        </div>
                    ) : (
                        <div className="transactions-table-container">
                            <table className="transactions-table">
                                <thead>
                                    <tr>
                                        <th>Date</th>
                                        <th>Type</th>
                                        <th>Source</th>
                                        <th className="text-right">Amount (USDT)</th>
                                        <th>Reference</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map(tx => (
                                        <tr key={tx.id} className={tx.transaction_type === 'credit' ? 'credit-row' : 'debit-row'}>
                                            <td>{formatDate(tx.transaction_date)}</td>
                                            <td>
                                                <span className={`transaction-type ${tx.transaction_type}`}>
                                                    {tx.transaction_type === 'credit' ? 
                                                        <><FontAwesomeIcon icon={faArrowUp} /> Credit</> : 
                                                        <><FontAwesomeIcon icon={faArrowDown} /> Debit</>
                                                    }
                                                </span>
                                            </td>
                                            <td>{tx.source_type === 'partner_transfer' ? 'Partner Transfer' : tx.source_type}</td>
                                            <td className={`text-right ${tx.transaction_type === 'credit' ? 'credit' : 'debit'}`}>
                                                {tx.transaction_type === 'credit' ? '+' : '-'}{formatCurrency(tx.amount, 'USDT')}
                                            </td>
                                            <td>{tx.reference_no || '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr className="summary-row">
                                        <td colSpan="3" className="text-right"><strong>Total Balance:</strong></td>
                                        <td className="text-right credit"><strong>{formatCurrency(totalBalance, 'USDT')}</strong></td>
                                        <td></td>
                                    </tr>
                                </tfoot>
                             </table>
                        </div>
                    )}
                </div>
                <div className="modal-footer">
                    <button className="btn-secondary" onClick={onClose}>Close</button>
                    <button className="btn-primary" onClick={() => window.print()}>
                        <FontAwesomeIcon icon={faPrint} /> Print
                    </button>
                </div>
            </div>
        </div>
    );
};

// ==============================================
// Main PartnerToOwner Component
// ==============================================
const PartnerToOwner = () => {
    const { user, currentShop } = useAuth();

    const [shopId] = useState(() => {
        if (currentShop?.shop_id) return currentShop.shop_id;
        if (user?.shop_id) return user.shop_id;
        return localStorage.getItem('current_shop_id') || '';
    });

    // Sidebar state
    const [allPartners, setAllPartners] = useState([]);
    const [selectedPartner, setSelectedPartner] = useState(null);
    const [loadingPartners, setLoadingPartners] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

    // Currency conversion state
    const [fromCurrency, setFromCurrency] = useState('PKR');
    const [amount, setAmount] = useState('');
    const [exchangeRate, setExchangeRate] = useState(null);
    const [convertedAmount, setConvertedAmount] = useState(0);
    const [loadingRate, setLoadingRate] = useState(false);
    const [exchangeRates, setExchangeRates] = useState({});
    
    // Form fields
    const [transferDate, setTransferDate] = useState(new Date().toISOString().split('T')[0]);
    const [referenceNo, setReferenceNo] = useState('');
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});

    // UI state
    const [activeTab, setActiveTab] = useState('form');
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [showOwnerTxModal, setShowOwnerTxModal] = useState(false);
    const [toast, setToast] = useState({ show: false, type: '', message: '' });
    const [partnerBalance, setPartnerBalance] = useState(0);

    const currentLanguage = localStorage.getItem('ourzest_language') || 'en';
    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    const showToast = (type, message) => {
        setToast({ show: true, type, message });
        setTimeout(() => setToast({ show: false }), 3000);
    };

    // Load exchange rates
    const loadExchangeRates = useCallback(async () => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_exchange_rates' })
            });
            if (!response.ok) throw new Error('HTTP error');
            const data = await response.json();
            if (data.status === 'success') {
                setExchangeRates(data.data);
            }
        } catch (err) {
            console.error('Error loading exchange rates:', err);
        }
    }, []);

    useEffect(() => {
        loadExchangeRates();
    }, [loadExchangeRates]);

    // Load all partners
    const loadAllPartners = useCallback(async () => {
        if (!shopId || !user?.username) return;
        setLoadingPartners(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'get_partners_for_transfer',
                    username: user.username,
                    shop_id: parseInt(shopId)
                })
            });
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            if (data.status === 'success') {
                setAllPartners(data.data || []);
            } else {
                setAllPartners([]);
            }
        } catch (err) {
            console.error('Error loading partners:', err);
            setAllPartners([]);
        } finally {
            setLoadingPartners(false);
        }
    }, [shopId, user?.username]);

    useEffect(() => {
        if (shopId && user?.username) loadAllPartners();
    }, [shopId, user?.username, loadAllPartners]);

    // Update exchange rate when fromCurrency changes
    useEffect(() => {
        const updateRate = async () => {
            if (!fromCurrency) return;
            setLoadingRate(true);
            try {
                // Get rate from exchangeRates state first
                if (exchangeRates[fromCurrency] && exchangeRates[fromCurrency]['USDT']) {
                    const rate = exchangeRates[fromCurrency]['USDT'];
                    setExchangeRate(rate);
                    if (amount) {
                        setConvertedAmount(parseFloat(amount) * rate);
                    }
                } else {
                    // Fetch from API
                    const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'get_exchange_rate',
                            from_currency: fromCurrency,
                            to_currency: 'USDT'
                        })
                    });
                    const data = await response.json();
                    if (data.status === 'success') {
                        setExchangeRate(data.data.rate);
                        if (amount) {
                            setConvertedAmount(parseFloat(amount) * data.data.rate);
                        }
                    }
                }
            } catch (err) {
                console.error('Error getting exchange rate:', err);
            } finally {
                setLoadingRate(false);
            }
        };
        updateRate();
    }, [fromCurrency, exchangeRates, amount]);

    // Update converted amount when amount changes
    useEffect(() => {
        if (amount && exchangeRate) {
            setConvertedAmount(parseFloat(amount) * exchangeRate);
        } else {
            setConvertedAmount(0);
        }
    }, [amount, exchangeRate]);

    // Load partner balance when selected partner changes
    useEffect(() => {
        const loadBalance = async () => {
            if (selectedPartner && fromCurrency) {
                try {
                    const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                            action: 'get_partner_balance',
                            partner_id: selectedPartner.partner_id,
                            shop_id: parseInt(shopId),
                            currency: fromCurrency
                        })
                    });
                    const data = await response.json();
                    if (data.status === 'success') {
                        setPartnerBalance(data.data.balance);
                    }
                } catch (err) {
                    console.error('Error loading partner balance:', err);
                }
            }
        };
        loadBalance();
    }, [selectedPartner, fromCurrency, shopId]);

    // Select partner
    const selectPartner = (partner) => {
        setSelectedPartner(partner);
        resetForm();
        setActiveTab('form');
    };

    // Reset form
    const resetForm = () => {
        setAmount('');
        setReferenceNo('');
        setNotes('');
        setErrors({});
        setFromCurrency(selectedPartner?.preferred_currency || 'PKR');
    };

    // Validate form
    const validateForm = () => {
        const newErrors = {};
        if (!selectedPartner) newErrors.partner = 'Please select a partner';
        if (!amount || parseFloat(amount) <= 0) newErrors.amount = 'Amount must be greater than 0';
        else if (partnerBalance !== undefined && parseFloat(amount) > partnerBalance) {
            newErrors.amount = `Amount exceeds available balance (${formatCurrency(partnerBalance, fromCurrency)})`;
        }
        if (!exchangeRate || exchangeRate <= 0) newErrors.rate = 'Invalid exchange rate';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handle transfer submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm() || saving) return;
        
        setSaving(true);
        try {
            const username = user?.username || localStorage.getItem('username');
            const transactionId = `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
            
            const payload = {
                action: 'transfer_to_owner',
                partner_id: selectedPartner.partner_id,
                shop_id: parseInt(shopId),
                original_currency: fromCurrency,
                original_amount: parseFloat(amount),
                converted_currency: 'USDT',
                exchange_rate: exchangeRate,
                transfer_date: transferDate,
                username,
                notes,
                reference_no: referenceNo,
                idempotency_key: transactionId
            };
            
            const response = await fetch('https://ourzestapp.online/oz_backend/partner_to_owner.php', {
                method: 'POST',
                headers: { 'Content-Type':'application/json' },
                body: JSON.stringify(payload)
            });
            
            if (!response.ok) throw new Error(`HTTP error: ${response.status}`);
            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', `Transfer completed! ${formatCurrency(parseFloat(amount), fromCurrency)} → ${formatCurrency(data.data.converted_amount, 'USDT')}`);
                resetForm();
                await loadAllPartners(); // Refresh partner list
                // Update selected partner balance
                if (selectedPartner) {
                    const updatedPartner = allPartners.find(p => p.partner_id === selectedPartner.partner_id);
                    if (updatedPartner) {
                        setSelectedPartner(updatedPartner);
                        setPartnerBalance(updatedPartner.current_balance);
                    }
                }
            } else {
                showToast('error', data.message || 'Failed to complete transfer');
            }
        } catch (err) {
            console.error('Error during transfer:', err);
            showToast('error', 'Network error occurred');
        } finally {
            setSaving(false);
        }
    };

    const filteredPartners = allPartners.filter(p => {
        if (!searchTerm) return true;
        const sl = searchTerm.toLowerCase();
        return (
            p.partner_name?.toLowerCase().includes(sl) ||
            p.business_name?.toLowerCase().includes(sl) ||
            p.phone?.includes(searchTerm)
        );
    });

    const getCurrencyIcon = (currencyCode) => {
        const currency = CURRENCIES.find(c => c.code === currencyCode);
        return currency?.icon || faDollarSign;
    };

    const getCurrencySymbol = (currencyCode) => {
        const currency = CURRENCIES.find(c => c.code === currencyCode);
        return currency?.symbol || currencyCode;
    };

    return (
        <div className={`partner-to-owner-container ${isRTL ? 'rtl' : ''}`}>
            <ToastNotification toast={toast} setToast={setToast} />

            <TransferHistoryModal
                show={showHistoryModal}
                onClose={() => setShowHistoryModal(false)}
                shopId={shopId}
                partnerId={selectedPartner?.partner_id}
                partnerName={selectedPartner?.partner_name}
            />

            <OwnerTransactionsModal
                show={showOwnerTxModal}
                onClose={() => setShowOwnerTxModal(false)}
                shopId={shopId}
            />

            {/* HEADER */}
            <div className="account-header">
                <div className="header-content">
                    <div className="header-icon">
                        <FontAwesomeIcon icon={faExchangeAlt} />
                    </div>
                    <div className="header-text">
                        <h1>Partner to Owner Transfer</h1>
                        <p>Convert partner funds to USDT and transfer to owner account</p>
                        {currentShop && (
                            <div className="shop-badge">
                                <FontAwesomeIcon icon={faTag} /> {currentShop.shop_name}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* MAIN LAYOUT */}
            <div className="partner-to-owner-main-layout">

                {/* LEFT SIDEBAR */}
                <div className={`partner-sidebar ${sidebarCollapsed ? 'collapsed' : ''}`}>
                    <div className="sidebar-header">
                        <button className="sidebar-toggle" onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
                            <FontAwesomeIcon icon={sidebarCollapsed ? faChevronRight : faChevronLeft} />
                        </button>
                        {!sidebarCollapsed && (
                            <div className="sidebar-title">
                                <FontAwesomeIcon icon={faBuilding} />
                                <span>Partners</span>
                            </div>
                        )}
                    </div>

                    {!sidebarCollapsed && (
                        <>
                            <div className="sidebar-search">
                                <div className="search-input-wrapper">
                                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                    <input
                                        type="text"
                                        placeholder="Search partners..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {loadingPartners && (
                                        <FontAwesomeIcon icon={faSpinner} spin className="search-spinner" />
                                    )}
                                </div>
                            </div>

                            <div className="partner-list">
                                {loadingPartners ? (
                                    <div className="loading-partners">
                                        <FontAwesomeIcon icon={faSpinner} spin />
                                        <span>Loading...</span>
                                    </div>
                                ) : filteredPartners.length === 0 ? (
                                    <div className="no-partners">
                                        <FontAwesomeIcon icon={faBuilding} />
                                        <span>No partners found</span>
                                    </div>
                                ) : (
                                    filteredPartners.map(partner => (
                                        <div
                                            key={partner.partner_id}
                                            className={`partner-list-item ${selectedPartner?.partner_id === partner.partner_id ? 'active' : ''}`}
                                            onClick={() => selectPartner(partner)}
                                        >
                                            <div className="partner-item-content">
                                                <div className="partner-item-name">
                                                    <FontAwesomeIcon icon={faBuilding} className="item-icon" />
                                                    <span className="partner-name-text">{partner.partner_name}</span>
                                                </div>
                                                {partner.business_name && (
                                                    <div className="partner-item-business">
                                                        <FontAwesomeIcon icon={faAddressCard} size="xs" /> {partner.business_name}
                                                    </div>
                                                )}
                                                {partner.phone && (
                                                    <div className="partner-item-phone">
                                                        <FontAwesomeIcon icon={faPhone} size="xs" /> {partner.phone}
                                                    </div>
                                                )}
                                                <div className={`partner-item-balance ${parseFloat(partner.current_balance) > 0 ? 'positive' : ''}`}>
                                                    <FontAwesomeIcon icon={faWallet} className="balance-icon" />
                                                    <span className="balance-label">Balance:</span>
                                                    <span className="balance-amount">
                                                        {formatCurrency(partner.current_balance, partner.preferred_currency || 'PKR')}
                                                    </span>
                                                </div>
                                                {partner.usdt_balance > 0 && (
                                                    <div className="partner-item-usdt">
                                                        <FontAwesomeIcon icon={faWallet} />
                                                        <span>USDT: {formatCurrency(partner.usdt_balance, 'USDT')}</span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </>
                    )}
                </div>

                {/* RIGHT MAIN CONTENT */}
                <div className="partner-main-content">
                    {selectedPartner ? (
                        <div className="transfer-content">

                            {/* Summary Cards */}
                            <div className="summary-cards">
                                <div className="summary-card">
                                    <div className="card-icon"><FontAwesomeIcon icon={faBuilding} /></div>
                                    <div className="card-content">
                                        <div className="card-label">Partner</div>
                                        <div className="card-value">{selectedPartner.partner_name}</div>
                                        {selectedPartner.phone && (
                                            <div className="card-subvalue">
                                                <FontAwesomeIcon icon={faPhone} size="xs" /> {selectedPartner.phone}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="summary-card balance-card">
                                    <div className="card-icon"><FontAwesomeIcon icon={faBalanceScale} /></div>
                                    <div className="card-content">
                                        <div className="card-label">Available Balance</div>
                                        <div className="card-value balance-value">
                                            {formatCurrency(partnerBalance, fromCurrency)}
                                        </div>
                                        <div className="card-subvalue">
                                            <FontAwesomeIcon icon={faWallet} size="xs" />
                                            Preferred Currency: {selectedPartner.preferred_currency || 'PKR'}
                                        </div>
                                    </div>
                                </div>

                                <div className="summary-card conversion-card">
                                    <div className="card-icon"><FontAwesomeIcon icon={faChartLine} /></div>
                                    <div className="card-content">
                                        <div className="card-label">Current Rate</div>
                                        <div className="card-value rate-value">
                                            {loadingRate ? (
                                                <FontAwesomeIcon icon={faSpinner} spin />
                                            ) : (
                                                `1 ${fromCurrency} = ${exchangeRate?.toFixed(6) || '?'} USDT`
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="action-buttons">
                                <button className="btn-secondary" onClick={() => setShowHistoryModal(true)}>
                                    <FontAwesomeIcon icon={faHistory} /> Transfer History
                                </button>
                                <button className="btn-secondary" onClick={() => setShowOwnerTxModal(true)}>
                                    <FontAwesomeIcon icon={faWallet} /> Owner Transactions
                                </button>
                            </div>

                            {/* Tabs */}
                            <div className="transfer-tabs">
                                <button
                                    className={`tab-btn ${activeTab === 'form' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('form')}
                                >
                                    <FontAwesomeIcon icon={faExchangeAlt} /> Transfer Form
                                </button>
                                <button
                                    className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('info')}
                                >
                                    <FontAwesomeIcon icon={faInfoCircle} /> Conversion Info
                                </button>
                            </div>

                            {/* Tab Content */}
                            <div className="tab-content">

                                {/* FORM TAB */}
                                {activeTab === 'form' && (
                                    <form onSubmit={handleSubmit} className="transfer-form">

                                        {/* Currency Selection */}
                                        <div className="form-section">
                                            <h3 className="section-title">
                                                <FontAwesomeIcon icon={faMoneyBillWave} /> Currency Selection
                                            </h3>
                                            <div className="currency-selector">
                                                {CURRENCIES.filter(c => c.code !== 'USDT').map(currency => (
                                                    <button
                                                        key={currency.code}
                                                        type="button"
                                                        className={`currency-btn ${fromCurrency === currency.code ? 'active' : ''}`}
                                                        onClick={() => setFromCurrency(currency.code)}
                                                    >
                                                        <FontAwesomeIcon icon={currency.icon} />
                                                        <span>{currency.code}</span>
                                                        <small>{currency.name}</small>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Amount Input */}
                                        <div className="form-section">
                                            <h3 className="section-title">
                                                <FontAwesomeIcon icon={faCalculator} /> Transfer Details
                                            </h3>
                                            <div className="form-grid">
                                                <div className={`form-group ${errors.amount ? 'has-error' : ''}`}>
                                                    <div className="input-container">
                                                        <FontAwesomeIcon icon={getCurrencyIcon(fromCurrency)} className="input-icon" />
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            value={amount}
                                                            onChange={(e) => setAmount(e.target.value)}
                                                            placeholder=" "
                                                            dir="ltr"
                                                        />
                                                        <label className="input-label">
                                                            Amount ({getCurrencySymbol(fromCurrency)})
                                                        </label>
                                                    </div>
                                                    {errors.amount && (
                                                        <div className="error-message">
                                                            <FontAwesomeIcon icon={faExclamationTriangle} /> {errors.amount}
                                                        </div>
                                                    )}
                                                    <div className="info-text">
                                                        <FontAwesomeIcon icon={faInfoCircle} /> Available: {formatCurrency(partnerBalance, fromCurrency)}
                                                    </div>
                                                </div>

                                                <div className="conversion-preview">
                                                    <div className="preview-arrow">
                                                        <FontAwesomeIcon icon={faArrowRight} />
                                                    </div>
                                                    <div className="preview-result">
                                                        <div className="preview-label">You'll Receive</div>
                                                        <div className="preview-amount">
                                                            {formatCurrency(convertedAmount, 'USDT')}
                                                        </div>
                                                        <div className="preview-rate">
                                                            Rate: 1 {fromCurrency} = {exchangeRate?.toFixed(6) || '?'} USDT
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Transaction Details */}
                                        <div className="form-section">
                                            <h3 className="section-title">
                                                <FontAwesomeIcon icon={faFileInvoice} /> Transaction Details
                                            </h3>
                                            <div className="form-grid">
                                                <div className="form-group">
                                                    <div className="input-container">
                                                        <FontAwesomeIcon icon={faCalendarAlt} className="input-icon" />
                                                        <input
                                                            type="date"
                                                            value={transferDate}
                                                            onChange={(e) => setTransferDate(e.target.value)}
                                                            dir="ltr"
                                                        />
                                                        <label className="input-label">Transfer Date</label>
                                                    </div>
                                                </div>

                                                <div className="form-group">
                                                    <div className="input-container">
                                                        <FontAwesomeIcon icon={faBarcode} className="input-icon" />
                                                        <input
                                                            type="text"
                                                            value={referenceNo}
                                                            onChange={(e) => setReferenceNo(e.target.value)}
                                                            placeholder=" "
                                                            dir={isRTL ? 'rtl' : 'ltr'}
                                                        />
                                                        <label className="input-label">Reference No</label>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="form-group full-width">
                                                <div className="input-container textarea-container">
                                                    <FontAwesomeIcon icon={faReceipt} className="input-icon" />
                                                    <textarea
                                                        value={notes}
                                                        onChange={(e) => setNotes(e.target.value)}
                                                        placeholder=" "
                                                        rows="3"
                                                        dir={isRTL ? 'rtl' : 'ltr'}
                                                    />
                                                    <label className="input-label">Notes</label>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Transfer Summary */}
                                        {amount && convertedAmount > 0 && (
                                            <div className="transfer-summary">
                                                <h4>Transfer Summary</h4>
                                                <div className="summary-row">
                                                    <span>Partner:</span>
                                                    <strong>{selectedPartner.partner_name}</strong>
                                                </div>
                                                <div className="summary-row">
                                                    <span>Amount to Debit:</span>
                                                    <strong>{formatCurrency(parseFloat(amount), fromCurrency)}</strong>
                                                </div>
                                                <div className="summary-row">
                                                    <span>Exchange Rate:</span>
                                                    <strong>1 {fromCurrency} = {exchangeRate?.toFixed(6)} USDT</strong>
                                                </div>
                                                <div className="summary-row highlight">
                                                    <span>Owner Receives:</span>
                                                    <strong className="usdt-amount">{formatCurrency(convertedAmount, 'USDT')}</strong>
                                                </div>
                                            </div>
                                        )}

                                        {/* Form Actions */}
                                        <div className="form-actions">
                                            <button type="button" className="btn-secondary" onClick={resetForm}>
                                                Cancel
                                            </button>
                                            <button
                                                type="submit"
                                                className="btn-primary"
                                                disabled={saving || !amount || parseFloat(amount) <= 0 || convertedAmount <= 0}
                                            >
                                                {saving ? (
                                                    <><FontAwesomeIcon icon={faSpinner} spin /> Processing...</>
                                                ) : (
                                                    <><FontAwesomeIcon icon={faExchangeAlt} /> Transfer to Owner</>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                )}

                                {/* INFO TAB */}
                                {activeTab === 'info' && (
                                    <div className="info-tab-content">
                                        <div className="info-card">
                                            <h4><FontAwesomeIcon icon={faInfoCircle} /> About Partner to Owner Transfer</h4>
                                            <p>This feature allows you to convert partner funds from their preferred currency to USDT and transfer them to the owner's account.</p>
                                        </div>

                                        <div className="info-card">
                                            <h4><FontAwesomeIcon icon={faChartLine} /> Current Conversion Rates</h4>
                                            <div className="rates-table">
                                                {CURRENCIES.filter(c => c.code !== 'USDT').map(currency => (
                                                    <div key={currency.code} className="rate-row">
                                                        <div className="rate-from">
                                                            <FontAwesomeIcon icon={currency.icon} />
                                                            <span>1 {currency.code}</span>
                                                        </div>
                                                        <div className="rate-arrow">
                                                            <FontAwesomeIcon icon={faArrowRight} />
                                                        </div>
                                                        <div className="rate-to">
                                                            <span>
                                                                {exchangeRates[currency.code]?.['USDT'] 
                                                                    ? `${exchangeRates[currency.code]['USDT'].toFixed(6)} USDT` 
                                                                    : 'Loading...'}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        <div className="info-card">
                                            <h4><FontAwesomeIcon icon={faCheckCircle} /> How It Works</h4>
                                            <ol>
                                                <li>Select a partner from the left sidebar</li>
                                                <li>Choose the currency you want to convert from</li>
                                                <li>Enter the amount to transfer</li>
                                                <li>The system will automatically calculate the USDT equivalent</li>
                                                <li>Click "Transfer to Owner" to complete the transaction</li>
                                                <li>The partner's balance will be reduced, and owner's USDT balance will increase</li>
                                            </ol>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="no-partner-selected">
                            <FontAwesomeIcon icon={faBuilding} className="empty-icon" />
                            <h3>Select a Partner</h3>
                            <p>Select a partner from the list to convert funds and transfer to owner</p>
                            <p className="hint-text">
                                <FontAwesomeIcon icon={faExchangeAlt} /> Convert PKR, DIRHAMS, or YANS to USDT
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Loading Overlay */}
            {saving && (
                <div className="loading-overlay">
                    <div className="loading-content">
                        <div className="loading-spinner"></div>
                        <p>Processing transfer...</p>
                    </div>
                </div>
            )}

            <style jsx>{`
                .partner-to-owner-container {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    background: #f5f6fa;
                }
                .account-header {
                    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                    padding: 24px;
                    color: white;
                    flex-shrink: 0;
                }
                .header-content {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    flex-wrap: wrap;
                }
                .header-icon {
                    width: 60px;
                    height: 60px;
                    background: rgba(255,255,255,0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 28px;
                    flex-shrink: 0;
                }
                .header-text {
                    flex: 1;
                }
                .header-text h1 {
                    margin: 0;
                    font-size: 24px;
                    font-weight: 600;
                }
                .header-text p {
                    margin: 4px 0 0;
                    opacity: 0.9;
                    font-size: 14px;
                }
                .shop-badge {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    background: rgba(255,255,255,0.2);
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 12px;
                    margin-top: 8px;
                }
                .partner-to-owner-main-layout {
                    display: flex;
                    flex: 1;
                    overflow: hidden;
                    min-height: 0;
                }
                .partner-sidebar {
                    width: 280px;
                    background: white;
                    border-right: 1px solid #e9ecef;
                    display: flex;
                    flex-direction: column;
                    transition: width 0.3s ease;
                    flex-shrink: 0;
                }
                .partner-sidebar.collapsed {
                    width: 48px;
                }
                .sidebar-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 12px;
                    border-bottom: 1px solid #e9ecef;
                    min-height: 52px;
                }
                .sidebar-toggle {
                    background: none;
                    border: none;
                    cursor: pointer;
                    color: #666;
                    padding: 4px;
                    font-size: 14px;
                    flex-shrink: 0;
                }
                .sidebar-title {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    color: #333;
                    font-size: 14px;
                    white-space: nowrap;
                    overflow: hidden;
                }
                .sidebar-search {
                    padding: 12px;
                    border-bottom: 1px solid #e9ecef;
                }
                .search-input-wrapper {
                    position: relative;
                    display: flex;
                    align-items: center;
                }
                .search-input-wrapper .search-icon {
                    position: absolute;
                    left: 10px;
                    color: #999;
                    font-size: 13px;
                }
                .sidebar-search input {
                    width: 100%;
                    padding: 8px 8px 8px 32px;
                    border: 1px solid #ddd;
                    border-radius: 6px;
                    font-size: 13px;
                    outline: none;
                    box-sizing: border-box;
                }
                .search-spinner {
                    position: absolute;
                    right: 10px;
                    color: #11998e;
                    font-size: 13px;
                }
                .partner-list {
                    flex: 1;
                    overflow-y: auto;
                }
                .loading-partners, .no-partners {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 8px;
                    padding: 24px;
                    color: #999;
                    font-size: 13px;
                    text-align: center;
                }
                .partner-list-item {
                    padding: 12px;
                    cursor: pointer;
                    border-bottom: 1px solid #f0f0f0;
                    transition: background 0.15s;
                }
                .partner-list-item:hover {
                    background: #f8f9fa;
                }
                .partner-list-item.active {
                    background: #e8f5e9;
                    border-left: 3px solid #11998e;
                }
                .partner-item-name {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 500;
                    font-size: 13px;
                    color: #333;
                    margin-bottom: 4px;
                }
                .item-icon {
                    color: #11998e;
                    font-size: 12px;
                    flex-shrink: 0;
                }
                .partner-name-text {
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                }
                .partner-item-business, .partner-item-phone {
                    font-size: 11px;
                    color: #888;
                    margin-bottom: 2px;
                }
                .partner-item-balance {
                    font-size: 12px;
                    margin-top: 6px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                    background: #f8f9fa;
                    padding: 4px 8px;
                    border-radius: 4px;
                }
                .partner-item-balance .balance-icon {
                    color: #11998e;
                    font-size: 10px;
                }
                .partner-item-balance .balance-label {
                    color: #666;
                }
                .partner-item-balance .balance-amount {
                    font-weight: 600;
                }
                .partner-item-balance.positive .balance-amount {
                    color: #e53e3e;
                }
                .partner-item-usdt {
                    font-size: 11px;
                    color: #11998e;
                    margin-top: 4px;
                    display: flex;
                    align-items: center;
                    gap: 6px;
                }
                .partner-main-content {
                    flex: 1;
                    overflow-y: auto;
                    padding: 20px;
                    min-width: 0;
                }
                .summary-cards {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
                    gap: 16px;
                    margin-bottom: 20px;
                }
                .summary-card {
                    background: white;
                    border-radius: 10px;
                    padding: 16px;
                    display: flex;
                    gap: 14px;
                    align-items: flex-start;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                    border: 1px solid #e9ecef;
                }
                .summary-card.balance-card {
                    border-top: 3px solid #11998e;
                }
                .summary-card.conversion-card {
                    border-top: 3px solid #38ef7d;
                }
                .card-icon {
                    width: 40px;
                    height: 40px;
                    background: #e8f5e9;
                    border-radius: 8px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #11998e;
                    font-size: 16px;
                    flex-shrink: 0;
                }
                .card-content {
                    flex: 1;
                    min-width: 0;
                }
                .card-label {
                    font-size: 11px;
                    color: #888;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    margin-bottom: 4px;
                }
                .card-value {
                    font-size: 16px;
                    font-weight: 700;
                    color: #1a1a2e;
                }
                .balance-value {
                    color: #11998e;
                }
                .rate-value {
                    font-size: 14px;
                }
                .card-subvalue {
                    font-size: 11px;
                    color: #888;
                    margin-top: 3px;
                }
                .action-buttons {
                    display: flex;
                    gap: 12px;
                    margin-bottom: 20px;
                    flex-wrap: wrap;
                }
                .transfer-tabs {
                    display: flex;
                    gap: 4px;
                    border-bottom: 2px solid #e9ecef;
                }
                .tab-btn {
                    padding: 10px 20px;
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-size: 14px;
                    color: #888;
                    border-bottom: 2px solid transparent;
                    margin-bottom: -2px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                    white-space: nowrap;
                }
                .tab-btn:hover {
                    color: #11998e;
                }
                .tab-btn.active {
                    color: #11998e;
                    border-bottom-color: #11998e;
                    font-weight: 600;
                }
                .tab-content {
                    background: white;
                    border-radius: 0 0 10px 10px;
                    padding: 20px;
                    box-shadow: 0 1px 4px rgba(0,0,0,0.08);
                    border: 1px solid #e9ecef;
                    border-top: none;
                }
                .form-section {
                    margin-bottom: 24px;
                }
                .section-title {
                    font-size: 15px;
                    font-weight: 600;
                    color: #333;
                    margin: 0 0 14px 0;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .currency-selector {
                    display: flex;
                    gap: 12px;
                    flex-wrap: wrap;
                }
                .currency-btn {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 6px;
                    padding: 12px 20px;
                    border: 2px solid #ddd;
                    border-radius: 10px;
                    background: white;
                    cursor: pointer;
                    transition: all 0.2s;
                    min-width: 100px;
                }
                .currency-btn svg {
                    font-size: 24px;
                    color: #666;
                }
                .currency-btn span {
                    font-weight: 600;
                    font-size: 16px;
                }
                .currency-btn small {
                    font-size: 10px;
                    color: #999;
                }
                .currency-btn.active {
                    border-color: #11998e;
                    background: #e8f5e9;
                }
                .currency-btn.active svg {
                    color: #11998e;
                }
                .form-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 20px;
                }
                .form-group {
                    position: relative;
                }
                .form-group.full-width {
                    grid-column: 1 / -1;
                }
                .form-group.has-error .input-container input {
                    border-color: #dc3545;
                }
                .input-container {
                    position: relative;
                }
                .input-container input,
                .input-container textarea {
                    width: 100%;
                    padding: 14px 12px 6px 40px;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    font-size: 14px;
                    transition: border-color 0.2s, box-shadow 0.2s;
                    box-sizing: border-box;
                    background: white;
                }
                .input-container input:focus,
                .input-container textarea:focus {
                    outline: none;
                    border-color: #11998e;
                    box-shadow: 0 0 0 3px rgba(17,153,142,0.15);
                }
                .input-container .input-icon {
                    position: absolute;
                    left: 13px;
                    top: 50%;
                    transform: translateY(-50%);
                    color: #aaa;
                    font-size: 14px;
                    pointer-events: none;
                    z-index: 1;
                }
                .input-container.textarea-container .input-icon {
                    top: 18px;
                    transform: none;
                }
                .input-container label.input-label {
                    position: absolute;
                    left: 40px;
                    top: 50%;
                    transform: translateY(-50%);
                    background: white;
                    padding: 0 4px;
                    color: #aaa;
                    font-size: 14px;
                    transition: all 0.2s;
                    pointer-events: none;
                    z-index: 1;
                }
                .input-container input:focus ~ label.input-label,
                .input-container input:not(:placeholder-shown) ~ label.input-label,
                .input-container textarea:focus ~ label.input-label,
                .input-container textarea:not(:placeholder-shown) ~ label.input-label {
                    top: 4px;
                    transform: none;
                    font-size: 10px;
                    color: #11998e;
                }
                .error-message {
                    color: #dc3545;
                    font-size: 12px;
                    margin-top: 5px;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                .info-text {
                    font-size: 12px;
                    color: #666;
                    margin-top: 5px;
                    display: flex;
                    align-items: center;
                    gap: 5px;
                }
                .conversion-preview {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    background: #f8f9fa;
                    border-radius: 12px;
                    padding: 20px;
                }
                .preview-arrow {
                    font-size: 32px;
                    color: #11998e;
                }
                .preview-result {
                    flex: 1;
                    text-align: center;
                }
                .preview-label {
                    font-size: 12px;
                    color: #888;
                    margin-bottom: 5px;
                }
                .preview-amount {
                    font-size: 24px;
                    font-weight: 700;
                    color: #11998e;
                }
                .preview-rate {
                    font-size: 12px;
                    color: #666;
                    margin-top: 5px;
                }
                .transfer-summary {
                    background: #e8f5e9;
                    border-radius: 12px;
                    padding: 16px;
                    margin: 20px 0;
                }
                .transfer-summary h4 {
                    margin: 0 0 12px 0;
                    font-size: 14px;
                    color: #2e7d32;
                }
                .summary-row {
                    display: flex;
                    justify-content: space-between;
                    padding: 8px 0;
                    border-bottom: 1px solid #c8e6c9;
                }
                .summary-row:last-child {
                    border-bottom: none;
                }
                .summary-row.highlight {
                    padding-top: 12px;
                    margin-top: 8px;
                    border-top: 2px solid #c8e6c9;
                    font-size: 16px;
                }
                .usdt-amount {
                    color: #11998e;
                    font-size: 18px;
                }
                .form-actions {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    padding-top: 20px;
                    border-top: 1px solid #e9ecef;
                }
                .btn-primary {
                    padding: 10px 24px;
                    background: #11998e;
                    border: none;
                    border-radius: 8px;
                    color: white;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                }
                .btn-primary:hover:not(:disabled) {
                    background: #0e7a72;
                    transform: translateY(-1px);
                }
                .btn-primary:disabled {
                    opacity: 0.6;
                    cursor: not-allowed;
                }
                .btn-secondary {
                    padding: 10px 24px;
                    background: #f5f5f5;
                    border: 1px solid #ddd;
                    border-radius: 8px;
                    cursor: pointer;
                    font-size: 14px;
                    font-weight: 500;
                    display: inline-flex;
                    align-items: center;
                    gap: 8px;
                    transition: all 0.2s;
                    color: #555;
                }
                .btn-secondary:hover {
                    background: #e9ecef;
                }
                .info-tab-content {
                    display: flex;
                    flex-direction: column;
                    gap: 20px;
                }
                .info-card {
                    background: #f8f9fa;
                    border-radius: 10px;
                    padding: 16px;
                }
                .info-card h4 {
                    margin: 0 0 12px 0;
                    color: #11998e;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .rates-table {
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                }
                .rate-row {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 20px;
                    padding: 10px;
                    background: white;
                    border-radius: 8px;
                }
                .rate-from {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                }
                .rate-arrow {
                    color: #11998e;
                }
                .info-card ol {
                    margin: 0;
                    padding-left: 20px;
                }
                .info-card li {
                    margin: 8px 0;
                    color: #555;
                }
                .no-partner-selected {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    justify-content: center;
                    padding: 60px 20px;
                    text-align: center;
                    color: #bbb;
                    height: 100%;
                }
                .empty-icon {
                    font-size: 64px;
                    margin-bottom: 20px;
                    color: #ddd;
                }
                .hint-text {
                    margin-top: 12px;
                    font-size: 12px;
                    color: #11998e;
                }
                .loading-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(255,255,255,0.7);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 9999;
                }
                .loading-spinner {
                    width: 40px;
                    height: 40px;
                    border: 3px solid #e9ecef;
                    border-top-color: #11998e;
                    border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                    margin: 0 auto 12px;
                }
                @keyframes spin {
                    to { transform: rotate(360deg); }
                }
                .modal-overlay {
                    position: fixed;
                    inset: 0;
                    background: rgba(0,0,0,0.5);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    z-index: 2000;
                }
                .modal-content {
                    background: white;
                    border-radius: 12px;
                    display: flex;
                    flex-direction: column;
                }
                .modal-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 16px 20px;
                    border-bottom: 1px solid #e9ecef;
                }
                .modal-close {
                    background: none;
                    border: none;
                    font-size: 22px;
                    cursor: pointer;
                    color: #999;
                }
                .modal-body {
                    padding: 20px;
                    flex: 1;
                    overflow-y: auto;
                }
                .modal-footer {
                    display: flex;
                    justify-content: flex-end;
                    gap: 12px;
                    padding: 16px 20px;
                    border-top: 1px solid #e9ecef;
                }
                .loading-state, .error-state, .empty-state {
                    text-align: center;
                    padding: 40px;
                    color: #999;
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                    gap: 12px;
                }
                .toast-notification {
                    position: fixed;
                    top: 20px;
                    right: 20px;
                    z-index: 9999;
                    animation: slideIn 0.3s ease;
                }
                @keyframes slideIn {
                    from { transform: translateX(110%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                .toast-content {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 20px;
                    color: white;
                    border-radius: 10px;
                    box-shadow: 0 4px 16px rgba(0,0,0,0.2);
                    min-width: 260px;
                }
                .toast-notification.success .toast-content {
                    background: #28a745;
                }
                .toast-notification.error .toast-content {
                    background: #dc3545;
                }
                .transfers-table-container, .transactions-table-container {
                    overflow-x: auto;
                }
                .transfers-table, .transactions-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }
                .transfers-table th, .transfers-table td,
                .transactions-table th, .transactions-table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #f0f0f0;
                }
                .transfers-table th, .transactions-table th {
                    background: #f8f9fa;
                    font-weight: 600;
                    color: #555;
                    font-size: 12px;
                    text-transform: uppercase;
                }
                .text-right {
                    text-align: right !important;
                }
                .original-amount {
                    color: #e67e22;
                    font-weight: 500;
                }
                .converted-amount {
                    color: #11998e;
                    font-weight: 700;
                }
                .credit {
                    color: #28a745;
                    font-weight: 600;
                }
                .debit {
                    color: #dc3545;
                    font-weight: 600;
                }
                .transaction-type {
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    border-radius: 20px;
                    font-size: 11px;
                    font-weight: 600;
                }
                .transaction-type.credit {
                    background: #e6f4ea;
                    color: #28a745;
                }
                .transaction-type.debit {
                    background: #fde8e8;
                    color: #dc3545;
                }
                .credit-row {
                    background: #f9fff9;
                }
                .debit-row {
                    background: #fff9f9;
                }
                .summary-row td {
                    border-top: 2px solid #e9ecef;
                    background: #f8f9fa;
                }
                .owner-balance-card {
                    display: flex;
                    align-items: center;
                    gap: 16px;
                    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
                    border-radius: 12px;
                    padding: 20px;
                    margin-bottom: 20px;
                    color: white;
                }
                .balance-icon {
                    width: 50px;
                    height: 50px;
                    background: rgba(255,255,255,0.2);
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 24px;
                }
                .balance-content {
                    flex: 1;
                }
                .balance-label {
                    font-size: 12px;
                    opacity: 0.9;
                    margin-bottom: 4px;
                }
                .balance-value {
                    font-size: 28px;
                    font-weight: 700;
                }
                .rtl .input-container .input-icon {
                    left: auto;
                    right: 13px;
                }
                .rtl .input-container label.input-label {
                    left: auto;
                    right: 40px;
                }
                .rtl .sidebar-search input {
                    padding: 8px 32px 8px 8px;
                }
                .rtl .search-input-wrapper .search-icon {
                    left: auto;
                    right: 10px;
                }
                .rtl .partner-list-item.active {
                    border-left: none;
                    border-right: 3px solid #11998e;
                }
                .rtl .text-right {
                    text-align: left !important;
                }
                .rtl .conversion-preview .preview-arrow svg {
                    transform: rotate(180deg);
                }
            `}</style>
        </div>
    );
};

export default PartnerToOwner;